import { atom } from "jotai";
import { Credit } from "@/core/entities/credit";
import { UploadCreditDocumentUseCase } from "@/core/usecases/credit";
import { CreditDocumentUploadRepository } from "@/infrastructure/repositories/credit";
import { updateCreditAtom } from "@/store/credit/credit.actions";
import { CreditUploadDocument, creditDocumentUploadAtom } from "./documentUpload.atoms";

export const addCreditDocumentUploadAtom = atom(null, async (get, set, documents: CreditUploadDocument[]) => {
  set(creditDocumentUploadAtom, {
    ...get(creditDocumentUploadAtom),
    documents: [...get(creditDocumentUploadAtom).documents, ...documents],
  });
});

export const clearCreditDocumentUploadAtom = atom(null, async (get, set) => {
  set(creditDocumentUploadAtom, {
    ...get(creditDocumentUploadAtom),
    documents: [],
  });
});

export const removeCreditDocumentAtom = atom(null, async (get, set, documentId: number) => {
  set(creditDocumentUploadAtom, {
    ...get(creditDocumentUploadAtom),
    documents: get(creditDocumentUploadAtom).documents.filter(d => d.processId !== documentId),
  });
});

export const uploadCreditDocumentAtom = atom(null, async (get, set) => {
  const documents = get(creditDocumentUploadAtom).documents;
  if (documents.length === 0) {
    return;
  }

  const uploadDocumentUseCase = new UploadCreditDocumentUseCase(new CreditDocumentUploadRepository());
  const reversedDocuments = [...documents].reverse();

  for (const document of reversedDocuments) {
    const documentCheck = get(creditDocumentUploadAtom).documents.find(d => d.processId === document.processId);
    if (!documentCheck || document.isPending === false) {
      continue;
    }

    set(creditDocumentUploadAtom, {
      ...get(creditDocumentUploadAtom),
      documents: get(creditDocumentUploadAtom).documents.map(d =>
        d.processId === document.processId ? { ...d, isUploading: true } : { ...d, isUploading: false }
      ),
    });

    try {
      const responseDocument = await uploadDocumentUseCase.execute(document.file);
      set(creditDocumentUploadAtom, {
        ...get(creditDocumentUploadAtom),
        documents: get(creditDocumentUploadAtom).documents.map(d =>
          d.processId === document.processId
            ? { ...d, response: responseDocument, isUploading: false, isUploaded: true, isPending: false }
            : d
        ),
      });
    } catch {
      set(creditDocumentUploadAtom, {
        ...get(creditDocumentUploadAtom),
        documents: get(creditDocumentUploadAtom).documents.map(d =>
          d.processId === document.processId
            ? { ...d, isUploading: false, isPending: false, error: "Failed to upload document" }
            : d
        ),
      });
    }
  }
});

export const openCreditDocumentUploadAtom = atom(null, async (get, set, remittanceItemId: number) => {
  set(creditDocumentUploadAtom, {
    ...get(creditDocumentUploadAtom),
    isDocumentUploadOpen: true,
    selectedModalId: remittanceItemId,
  });
});

export const closeCreditDocumentUploadAtom = atom(null, async (get, set) => {
  set(creditDocumentUploadAtom, {
    ...get(creditDocumentUploadAtom),
    isDocumentUploadOpen: false,
    selectedModalId: null,
  });
});

export const setCreditIsAttachingAtom = atom(null, async (get, set, isAttaching: boolean) => {
  set(creditDocumentUploadAtom, {
    ...get(creditDocumentUploadAtom),
    isAttaching,
  });
});

export const updateCreditAfterUploadAtom = atom(null, (_get, set, credit: Credit) => {
  set(updateCreditAtom, credit);
});
